// Line check photos in the office (migration 034): a spec parameter's REFERENCE photos ("what good
// looks like", signed with the version) and a reading's ACTUAL photos (what one person saw).
//
// A photo is evidence, never a judgement: nothing here compares, scores or gates. Bytes are read
// through the API with the bearer token (`api.blob`) and shown from an object URL - the container
// is never public, so an <img src> to it could not work and must not. Rule 15 throughout: a photo
// that could not be read says so with Try again, and a list the server did not send is "not
// known", never "no photos".
import { useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { fieldClass, labelClass, officeFocus, serverMessage } from '../../shells/OfficeShell';
import { scalePhoto } from '../operator/photoScale';
import { uploadReferencePhoto } from './linePhotoUpload';

export interface ReferencePhoto {
  photoId: number;
  caption: string | null;
  sha256: string;
}

export interface ReadingPhoto {
  photoId: number;
  uploadedByUpn: string;
  capturedAtDevice: string | null;
}

/** Most live reference photos one check may carry (the API's MAX_REFERENCE_PHOTOS). */
export const MAX_REFERENCE_PHOTOS = 6;

export const referencePhotoPath = (parameterId: number, photoId: number) => `/spec-parameters/${parameterId}/photos/${photoId}`;
export const readingPhotoPath = (readingId: number | string, photoId: number) => `/readings/${readingId}/photos/${photoId}`;

/** One photo's bytes, from the API, as an object URL; a failed read offers Try again. */
export function PhotoImage({
  path,
  alt,
  size,
  className,
  lazy = false,
  interactive = true,
}: {
  path: string;
  alt: string;
  size: 'thumb' | 'large';
  /** Replaces the size's box classes (a square tile, say). */
  className?: string;
  /** Fetch only when the photo is near the screen (a wall of photos). */
  lazy?: boolean;
  /** False inside a link or button: a failure shows text only, and pressing the parent retries. */
  interactive?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const holder = useRef<HTMLSpanElement | null>(null);
  const [near, setNear] = useState(!lazy);
  useEffect(() => {
    if (near || !holder.current || typeof IntersectionObserver === 'undefined') {
      if (!near) setNear(true);
      return;
    }
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setNear(true), { rootMargin: '400px' });
    io.observe(holder.current);
    return () => io.disconnect();
  }, [near]);
  useEffect(() => {
    if (!near) return;
    const ctrl = new AbortController();
    let made: string | null = null;
    setFailed(null);
    api
      .blob(path, ctrl.signal)
      .then((b) => {
        made = URL.createObjectURL(b);
        setUrl(made);
      })
      .catch((err) => {
        if (!ctrl.signal.aborted) setFailed(serverMessage(err));
      });
    return () => {
      ctrl.abort();
      if (made) URL.revokeObjectURL(made);
    };
  }, [path, attempt, near]);
  const box = className ?? (size === 'thumb' ? 'h-[48px] w-[48px]' : 'w-full max-h-[60vh]');
  if (url) {
    return <img src={url} alt={alt} decoding="async" className={`${box} rounded-(--radius-sm) border border-(--border) ${size === 'thumb' ? 'object-cover' : 'object-contain'}`} />;
  }
  return (
    <span
      ref={holder}
      className={`${className ?? (size === 'thumb' ? 'h-[48px] w-[48px] text-[10px]' : 'min-h-[120px] w-full text-(length:--fs-xs)')} flex flex-col items-center justify-center gap-[2px] rounded-(--radius-sm) border border-(--border) p-[2px] text-center text-(--muted)`}
      title={failed ?? undefined}
    >
      {failed && !interactive ? (
        <span>Not read: press to try again</span>
      ) : failed ? (
        <>
          <span>{size === 'thumb' ? 'Not read' : `Photo could not be read: ${failed}`}</span>
          <button
            type="button"
            className={`underline ${officeFocus}`}
            onClick={(e) => {
              e.stopPropagation();
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </button>
        </>
      ) : (
        'Loading…'
      )}
    </span>
  );
}

/**
 * A parameter's reference photos, in the spec's parameter table: thumbnails, and a sheet that
 * shows them full size with their captions. On a DRAFT, for a SPEC_APPROVE holder, the sheet adds
 * and retires them; an approved version's photos are as fixed as its limits (rule 6).
 */
export function ReferencePhotosCell({
  parameterId,
  parameterName,
  photos,
  canEdit,
  onChanged,
}: {
  parameterId: number;
  parameterName: string;
  /** Null = the server did not send them: not known, never "none". */
  photos: ReferencePhoto[] | null | undefined;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  if (!Array.isArray(photos)) {
    return (
      <span className="text-(length:--fs-xs) text-(--muted)">
        Not known{' '}
        <button type="button" className={`underline ${officeFocus}`} onClick={onChanged}>
          Try again
        </button>
      </span>
    );
  }
  const full = photos.length >= MAX_REFERENCE_PHOTOS;

  async function add() {
    if (!file) return;
    setBusy(true);
    setRefusal(null);
    try {
      const scaled = await scalePhoto(file);
      await uploadReferencePhoto(parameterId, scaled, caption);
      setFile(null);
      setCaption('');
      setInputKey((k) => k + 1);
      onChanged();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function retire(photoId: number) {
    setBusy(true);
    setRefusal(null);
    try {
      await api.post(`/spec-parameters/${parameterId}/photos/${photoId}/retire`, {});
      onChanged();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-(--s1)">
        {photos.slice(0, 3).map((p) => (
          <button
            key={p.photoId}
            type="button"
            className={`rounded-(--radius-sm) ${officeFocus}`}
            aria-label={`Open reference photo${p.caption ? `: ${p.caption}` : ''}`}
            onClick={() => setOpen(true)}
          >
            <PhotoImage path={referencePhotoPath(parameterId, p.photoId)} alt={p.caption ?? `Reference photo for ${parameterName}`} size="thumb" />
          </button>
        ))}
        {photos.length > 3 ? (
          <button type="button" className={`text-(length:--fs-xs) underline ${officeFocus}`} onClick={() => setOpen(true)}>
            +{photos.length - 3}
          </button>
        ) : null}
        {canEdit ? (
          <Button variant="outline" type="button" onClick={() => setOpen(true)}>
            Add reference photo
          </Button>
        ) : photos.length === 0 ? (
          '—'
        ) : null}
      </span>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-(--s3) overflow-y-auto sm:max-w-[min(720px,92vw)]">
          <SheetHeader>
            <SheetTitle>Reference photos · {parameterName}</SheetTitle>
            <SheetDescription>What meeting this check usually looks like. The limits are the spec; a photo is a picture of them.</SheetDescription>
          </SheetHeader>
          {photos.length === 0 ? <p className="m-0 text-(length:--fs-sm) text-(--muted)">No reference photos on this check.</p> : null}
          <ul className="m-0 flex list-none flex-col gap-(--s3) p-0">
            {photos.map((p) => (
              <li key={p.photoId} className="flex flex-col gap-(--s1)">
                <PhotoImage path={referencePhotoPath(parameterId, p.photoId)} alt={p.caption ?? `Reference photo for ${parameterName}`} size="large" />
                <span className="flex items-center justify-between gap-(--s2)">
                  <span className="text-(length:--fs-sm)">{p.caption ?? <span className="text-(--muted)">No caption</span>}</span>
                  {canEdit ? (
                    <Button variant="outline" type="button" disabled={busy} onClick={() => void retire(p.photoId)}>
                      Retire
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {canEdit ? (
            <div className="flex flex-col gap-(--s2) border-t border-(--border) pt-(--s3)">
              <label className="flex flex-col gap-(--s1)">
                <span className={labelClass}>Add reference photo</span>
                <input
                  key={inputKey}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  disabled={busy || full}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <label className="flex flex-col gap-(--s1)">
                <span className={labelClass}>Caption (optional)</span>
                <input className={`${fieldClass} ${officeFocus}`} value={caption} maxLength={200} disabled={busy || full} onChange={(e) => setCaption(e.target.value)} />
              </label>
              <span>
                <Button variant="default" type="button" disabled={busy || full || !file} onClick={() => void add()}>
                  {busy ? 'Sending…' : 'Upload photo'}
                </Button>
              </span>
              <p className="m-0 text-(length:--fs-xs) text-(--muted)">
                {full
                  ? `This check already has ${MAX_REFERENCE_PHOTOS} reference photos. Retire one first.`
                  : 'Approved with this draft. Scaled on this device to a JPEG of at most 2048 px.'}
              </p>
            </div>
          ) : null}
          {refusal ? (
            <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
              {refusal}
            </p>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function stamp(iso: string | null): string {
  if (!iso) return 'time not recorded';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * A reading's photos: a camera count in the readings table, and a sheet with Reference | What was
 * seen when the check has reference photos. A REJECTED reading's photos show the same way (rule 4:
 * the photo is the evidence of what the operator saw when the gate refused them).
 */
export function ReadingPhotosButton({
  readingId,
  checkName,
  photos,
  specParameterId,
  loadReference,
}: {
  readingId: number | string;
  checkName: string;
  photos: ReadingPhoto[] | null | undefined;
  specParameterId: number;
  /** The check's reference photos: null = the run's specs do not name it (actual photos only);
   *  a rejection = could not be read, said with Try again. */
  loadReference: (specParameterId: number) => Promise<{ parameterId: number; photos: ReferencePhoto[] } | null>;
}) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState<{ parameterId: number; photos: ReferencePhoto[] } | null | 'unknown'>(null);
  const [refAttempt, setRefAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let live = true;
    loadReference(specParameterId)
      .then((r) => live && setReference(r))
      .catch(() => live && setReference('unknown'));
    return () => {
      live = false;
    };
  }, [open, loadReference, specParameterId, refAttempt]);

  if (!Array.isArray(photos)) return <span className="text-(length:--fs-xs) text-(--muted)">Not known</span>;
  if (photos.length === 0) return <span className="text-(--muted)">—</span>;
  const refs = reference && reference !== 'unknown' ? reference.photos : [];

  return (
    <>
      <button
        type="button"
        className={`inline-flex items-center gap-[4px] rounded-(--radius-sm) border border-(--border) px-(--s2) py-[2px] text-(length:--fs-xs) font-semibold ${officeFocus}`}
        aria-label={`${photos.length} photo${photos.length === 1 ? '' : 's'} on this reading`}
        onClick={() => setOpen(true)}
      >
        <Camera className="h-[14px] w-[14px]" aria-hidden="true" />
        {photos.length}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-(--s3) overflow-y-auto sm:max-w-[min(1000px,92vw)]">
          <SheetHeader>
            <SheetTitle>Photos · {checkName}</SheetTitle>
            <SheetDescription>What was seen at this reading. A photo is evidence; the verdict is the spec's.</SheetDescription>
          </SheetHeader>
          <div className={`grid gap-(--s4) ${refs.length > 0 ? 'sm:grid-cols-2' : ''}`}>
            {refs.length > 0 && reference && reference !== 'unknown' ? (
              <section className="flex flex-col gap-(--s2)">
                <h3 className={labelClass}>Reference</h3>
                {refs.map((p) => (
                  <figure key={p.photoId} className="m-0 flex flex-col gap-(--s1)">
                    <PhotoImage path={referencePhotoPath(reference.parameterId, p.photoId)} alt={p.caption ?? `Reference photo for ${checkName}`} size="large" />
                    <figcaption className="text-(length:--fs-sm)">{p.caption ?? <span className="text-(--muted)">No caption</span>}</figcaption>
                  </figure>
                ))}
              </section>
            ) : null}
            <section className="flex flex-col gap-(--s2)">
              <h3 className={labelClass}>What was seen</h3>
              {photos.map((p) => (
                <figure key={p.photoId} className="m-0 flex flex-col gap-(--s1)">
                  <PhotoImage path={readingPhotoPath(readingId, p.photoId)} alt={`Photo on reading ${readingId}`} size="large" />
                  <figcaption className="text-(length:--fs-sm) text-(--muted)">
                    {p.uploadedByUpn} · {stamp(p.capturedAtDevice)}
                  </figcaption>
                </figure>
              ))}
            </section>
          </div>
          {reference === 'unknown' ? (
            <p className="m-0 text-(length:--fs-sm) text-(--warn)">
              The check's reference photos could not be read.{' '}
              <button type="button" className={`underline ${officeFocus}`} onClick={() => setRefAttempt((n) => n + 1)}>
                Try again
              </button>
            </p>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
